import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  FormControl,
  Select,
  Alert
} from '@mui/material';
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone';
import PersonIcon from '@mui/icons-material/Person';
import { theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;

const initialFormValues = {
  firstName: '',
  lastName: '',
  employeeId: '',
  phone: '',
  location: '',
  gender: ''
};

const schema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  employeeId: Yup.string()
    .required('Employee ID is required')
    .matches(/^OF\d{3}$/, 'Format must be OF followed by 3 digits (e.g. OF123)')
    .test('unique', 'This Employee ID is already assigned to another Branch Manager', function (value) {
      if (!value) return true;
      const { accounts = [], account } = this.options.context ?? {};
      const currentId = account?.id;
      return !accounts.some((a) => a.employeeId === value && a.id !== currentId);
    }),
  phone: Yup.string().required('Phone number is required').matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
  location: Yup.string().required('Location is required'),
  gender: Yup.string().required('Gender is required')
});

const Toast = ({ msg, severity = 'success', onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <Box sx={{ position: 'fixed', bottom: 24, left: 24, zIndex: 3000 }}>
      <Alert severity={severity} onClose={onDone}>
        {msg}
      </Alert>
    </Box>
  );
};

const outlinedColor = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': { borderColor: primary }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    legend: { display: 'none' }
  }
};

function BranchManagerForm({ open, onClose, onSave, account, accounts = [] }) {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: schema,
    context: { accounts, account },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const body = {
        firstName: values.firstName,
        lastName: values.lastName,
        employeeId: values.employeeId,
        mobile: values.phone,
        location: values.location,
        gender: values.gender
      };

      try {
        if (account) {
          await axiosInstance.put(`${nipponBotApiEndpoints.branchManagerAccountUpdate}/${account.id}`, body, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setToast({ msg: 'Branch Manager updated successfully.', severity: 'success' });
        } else {
          await axiosInstance.post(nipponBotApiEndpoints.branchManagerAccountCreate, body, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setToast({ msg: 'Branch Manager created successfully. Default password: 123456', severity: 'success' });
        }
        onSave(values);
        formik.resetForm();
        onClose();
      } catch (err) {
        setToast({ msg: err?.response?.data?.message || 'Failed to save Branch Manager.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (account) {
      formik.setValues({
        ...initialFormValues,
        firstName: account.firstName || '',
        lastName: account.lastName || '',
        employeeId: account.employeeId || '',
        phone: account.mobile || '',
        location: account.location || '',
        gender: account.gender || ''
      });
    } else {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  if (!open) return null;

  const Sec = ({ icon, label }) => (
    <Box display="flex" alignItems="center" gap={1} mb={2} mt={3}>
      {icon}
      <Typography variant="h6" fontWeight={500} fontSize="1rem">{label}</Typography>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: { xs: '100vw', sm: '80vw', md: '50vw', lg: '50vw' },
          maxWidth: '100vw',
          backgroundColor: '#fff',
          boxShadow: 24,
          zIndex: 1300,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            zIndex: 999
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>{account ? 'Edit Branch Manager' : 'Add New Branch Manager'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {account ? 'Edit a Branch Manager' : 'Create a new Branch Manager'} account for this Nippon Bot company
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <ClearTwoToneIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={formik.handleSubmit} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2, minHeight: 0 }}>
          <Sec icon={<PersonIcon sx={{ color: primary }} />} label="Basic Information" />

          <Box display="flex" gap={2} mb={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>First Name</Typography>
              <TextField
                size="small"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                sx={outlinedColor}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>Last Name</Typography>
              <TextField
                size="small"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                fullWidth
                sx={outlinedColor}
              />
            </Box>
          </Box>

          <Box display="flex" gap={2} mb={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>Employee ID</Typography>
              <TextField
                size="small"
                name="employeeId"
                placeholder="e.g. OF123"
                value={formik.values.employeeId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                helperText={formik.touched.employeeId && formik.errors.employeeId}
                fullWidth
                sx={outlinedColor}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>Phone</Typography>
              <TextField
                size="small"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                fullWidth
                sx={outlinedColor}
              />
            </Box>
          </Box>

          <Box mb={2}>
            <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>Location / Timezone</Typography>
            <TextField
              fullWidth
              size="small"
              name="location"
              value={formik.values.location}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.location && Boolean(formik.errors.location)}
              helperText={formik.touched.location && formik.errors.location}
              placeholder="e.g. New York, NY"
              sx={outlinedColor}
            />
          </Box>

          <Box mb={2}>
            <Typography fontWeight={500} fontSize="0.95rem" mb={0.5}>Gender</Typography>
            <FormControl size="small" fullWidth sx={outlinedColor}>
              <Select
                displayEmpty
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.gender && Boolean(formik.errors.gender)}
              >
                <MenuItem value="" disabled>Select Gender</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            {formik.touched.gender && formik.errors.gender && (
              <Typography variant="caption" color="error">{formik.errors.gender}</Typography>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={2}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: primary }}>
              {loading ? (account ? 'Updating…' : 'Creating…') : (account ? 'Update Branch Manager' : 'Create Branch Manager')}
            </Button>
          </Box>
        </Box>
      </Box>

      {toast && <Toast msg={toast.msg} severity={toast.severity} onDone={() => setToast(null)} />}
    </>
  );
}

export default BranchManagerForm;
